import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import '../styles/companionAppUsageReport.css';

const calculateChangePercent = (current, previous) => {
  if (!previous) {
    return 0;
  }
  return ((Number(current || 0) - Number(previous || 0)) / Number(previous)) * 100;
};

const formatNumber = (value, unit = '') => {
  const numericValue = Number(value || 0);
  const formattedValue = Number.isInteger(numericValue)
    ? numericValue.toLocaleString()
    : numericValue.toLocaleString(undefined, { maximumFractionDigits: 1 });

  return `${formattedValue}${unit}`;
};

const formatChange = (value) => {
  const numericValue = Number(value || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toFixed(1)}%`;
};

const DataSummaryTable = ({ dataSummary, isLoading = false, yearOverYearComparison = false }) => {
  if (isLoading) {
    return (
      <div className="companion-report-card page-break-avoid h-100">
        <Skeleton width="160px" height="20px" className="mb-3" />
        {[...Array(7)].map((_, index) => (
          <Skeleton key={index} height="29px" className="mb-2" />
        ))}
      </div>
    );
  }

  if (!dataSummary) {
    return null;
  }

  const rows = [
    { label: 'Daily Active Users', data: dataSummary.dailyActiveUsers },
    { label: 'Monthly Active Users', data: dataSummary.monthlyActiveUsers },
    { label: 'Average Session Length', data: dataSummary.averageSessionLength, unit: ' min' },
    { label: 'Sessions Per User', data: dataSummary.sessionsPerUser },
    { label: 'Day 1 Retention', data: dataSummary.day1Retention, unit: '%' },
    { label: 'Day 7 Retention', data: dataSummary.day7Retention, unit: '%' },
    { label: 'Day 30 Retention', data: dataSummary.day30Retention, unit: '%' }
  ];

  return (
    <div className="companion-report-card page-break-avoid h-100 companion-data-summary-card">
      <div className="companion-card-title-row">
        <div className="d-flex align-items-center">
          <h2 className="companion-card-title">Data Summary</h2>
          <OverlayTrigger
            placement="top"
            overlay={(
              <Tooltip id="data-summary-table-tooltip" className="custom-tooltip">
                <h6>Data Summary</h6>
                <span className="mb-0">
                  {yearOverYearComparison
                    ? 'Roll-up of every visible metric: current values, same period last year and % change.'
                    : 'Roll-up of every visible metric for the selected reporting period.'}
                  {yearOverYearComparison && <><br></br>Red row = YoY decline.</>}
                </span>
              </Tooltip>
            )}
          >
            <FontAwesomeIcon className="circle-question mx-1" icon={faCircleQuestion} />
          </OverlayTrigger>
        </div>
      </div>

      <div className="table-responsive">
        <table className="companion-data-summary-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Current</th>
              {yearOverYearComparison && <th>Prev. Year</th>}
              {yearOverYearComparison && <th>Change</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const current = row.data?.current || 0;
              const previous = row.data?.previous || 0;
              const changePercent = calculateChangePercent(current, previous);
              const isPositive = changePercent > 0;
              const isNegative = changePercent < 0;

              return (
                <tr
                  key={row.label}
                  className={yearOverYearComparison
                    ? isNegative ? 'companion-change-row-negative' : isPositive ? 'companion-change-row-positive' : ''
                    : ''}
                >
                  <td>{row.label}</td>
                  <td>{formatNumber(current, row.unit)}</td>
                  {yearOverYearComparison && <td>{formatNumber(previous, row.unit)}</td>}
                  {yearOverYearComparison && (
                    <td className={isNegative ? 'companion-change-negative' : isPositive ? 'companion-change-positive' : ''}>
                      {formatChange(changePercent)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

DataSummaryTable.propTypes = {
  dataSummary: PropTypes.shape({
    dailyActiveUsers: PropTypes.shape({ current: PropTypes.number, previous: PropTypes.number }),
    monthlyActiveUsers: PropTypes.shape({ current: PropTypes.number, previous: PropTypes.number }),
    averageSessionLength: PropTypes.shape({ current: PropTypes.number, previous: PropTypes.number }),
    sessionsPerUser: PropTypes.shape({ current: PropTypes.number, previous: PropTypes.number }),
    day1Retention: PropTypes.shape({ current: PropTypes.number, previous: PropTypes.number }),
    day7Retention: PropTypes.shape({ current: PropTypes.number, previous: PropTypes.number }),
    day30Retention: PropTypes.shape({ current: PropTypes.number, previous: PropTypes.number })
  }),
  isLoading: PropTypes.bool,
  yearOverYearComparison: PropTypes.bool
};

export default DataSummaryTable;